import * as stylex from '@stylexjs/stylex';

export const toolkitLayout = stylex.defineConsts({
  /**
   * The side padding `Dashboard.tsx` puts around every section (`px: 8`). The Toolkit undoes it so its
   * scroll area spans the whole column, then pads its content itself. Change both together.
   */
  dashboardPadding: '64px',
  dashboardPaddingUndo: '-64px',
  /** The Toolkit's own side padding on a phone-sized window, like the design. */
  narrowPadding: '16px',
  narrowPaddingUndo: '-16px',
  cardGap: '16px',
  sectionGap: '40px',
  /**
   * Width of a card that opens the generator: never wider than the design's 353px. The two cards share a
   * line until each would be narrower than 280px, then they stack.
   */
  entryCardMaxWidth: '353px',
  entryCardMinWidth: '280px',
  /** The design's tool card picture is 262 by 183. */
  pictureAspectRatio: '262 / 183',
});

/**
 * Hover and focus are read from these, through `stylex.when.ancestor`. A card and its row need separate
 * markers, or pointing anywhere at a row would light up every card in it.
 */
export const cardMarker = stylex.defineMarker();
export const rowMarker = stylex.defineMarker();

/**
 * Width of one tool card: never wider than the design's 262px, so a wide window shows more cards in a
 * row instead of bigger ones. When the content is too narrow for four at that size, four share it, then
 * two when it is narrower than 920px, then one when it is narrower than 368px. Measured on the content,
 * not the window, because the sidebar takes part of the window.
 * The ranges must not overlap: StyleX does not keep their order, so where two match, either can win.
 * `cqw` is the width of the Toolkit's content, which is a size container. A card keeps the same width
 * in a carousel and in an open row's grid. The 16px gaps are `toolkitLayout.cardGap`.
 */
export const cardSize = stylex.create({
  card: {
    flexShrink: 0,
    scrollSnapAlign: 'start',
    width: {
      default: 'min(262px, calc((100cqw - 48px) / 4))',
      '@container (min-width: 368px) and (max-width: 919px)': 'min(262px, calc((100cqw - 16px) / 2))',
      '@container (max-width: 367px)': 'min(262px, 100cqw)',
    },
  },
  /**
   * The height of a card's picture, so the carousel arrows sit in the middle of the pictures and not of the
   * whole card with its words. The widths are the ones in `card` above, times `pictureAspectRatio`, and
   * must change with them.
   */
  pictureBand: {
    height: {
      default: 'calc(min(262px, calc((100cqw - 48px) / 4)) * 183 / 262)',
      '@container (min-width: 368px) and (max-width: 919px)': 'calc(min(262px, calc((100cqw - 16px) / 2)) * 183 / 262)',
      '@container (max-width: 367px)': 'calc(min(262px, 100cqw) * 183 / 262)',
    },
  },
});
